import { AJUDA_ARQUIVOS } from "@/lib/ajuda/dialogos/arquivos";
import { useState } from "react";
import { Check, Copy, Link as LinkIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@bookdental/ui";
import { trpc } from "@/lib/trpc";

type ShareRow = {
  id: string;
  scope: "USER" | "ORGANIZATION" | "LINK";
  linkToken: string | null;
  expiresAt: string | Date | null;
  targetOrg: { id: string; name: string } | null;
  targetUser: { id: string; name: string } | null;
};

/** Espelha `LINK_SHARE_DEFAULT_TTL_DAYS` de `packages/schemas/src/storage/file.ts`, que é a fonte
 *  da verdade — este app não depende de @bookdental/schemas hoje. Mudou lá, mudar aqui. */
const LINK_SHARE_TTL_DIAS = 30;

/** "expira em 12 dias" / "expira hoje" / "expirado" — o link some sozinho, o usuário precisa saber. */
function descreverPrazo(expiresAt: string | Date): string {
  const dias = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000);
  if (dias < 0) return "expirado";
  if (dias === 0) return "expira hoje";
  return `expira em ${dias} ${dias === 1 ? "dia" : "dias"}`;
}

/** scope USER fica de fora da UI por enquanto (schema já suporta) — o caso de uso real hoje é
 *  compartilhar com uma organização parceira já vinculada, ou gerar um link público. */
export function ShareDialog({ fileId, onClose }: { fileId: string | null; onClose: () => void }) {
  const utils = trpc.useUtils();
  const [targetOrgId, setTargetOrgId] = useState("");
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const orgsQuery = trpc.file.listShareableOrganizations.useQuery(undefined, { enabled: !!fileId });
  const sharesQuery = trpc.file.listShares.useQuery({ fileId: fileId ?? "" }, { enabled: !!fileId });

  const invalidate = () => {
    utils.file.listShares.invalidate({ fileId: fileId ?? "" });
    utils.file.invalidate();
  };

  const share = trpc.file.share.useMutation({
    onSuccess: () => {
      toast.success("Compartilhado.");
      setTargetOrgId("");
      invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao compartilhar."),
  });
  const unshare = trpc.file.unshare.useMutation({
    onSuccess: () => {
      toast.success("Compartilhamento revogado.");
      invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao revogar."),
  });

  const shares = (sharesQuery.data ?? []) as ShareRow[];

  function handleCopyLink(token: string) {
    const url = `${window.location.origin}/compartilhado/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    });
  }

  return (
    <Dialog open={!!fileId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_ARQUIVOS.compartilhar}>Compartilhar arquivo</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="mb-1.5 text-[11px] uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
              Compartilhar com organização parceira
            </p>
            <div className="flex gap-2">
              <Select value={targetOrgId} onValueChange={setTargetOrgId}>
                <SelectTrigger className="h-9 flex-1 bg-white dark:bg-card">
                  <SelectValue placeholder="Selecione uma organização..." />
                </SelectTrigger>
                <SelectContent className="border-zinc-200 bg-white shadow-lg dark:border-border dark:bg-card">
                  {(orgsQuery.data ?? []).map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                disabled={!targetOrgId || share.isPending}
                onClick={() => fileId && share.mutate({ fileId, scope: "ORGANIZATION", targetOrgId })}
                className="inline-flex h-9 items-center rounded-md bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                Compartilhar
              </button>
            </div>
            {orgsQuery.data?.length === 0 && (
              <p className="mt-1.5 text-xs text-zinc-400 dark:text-muted-foreground">
                Nenhuma organização parceira vinculada ainda.
              </p>
            )}
          </div>

          <div>
            <p className="mb-1.5 text-[11px] uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
              Link público
            </p>
            <button
              type="button"
              disabled={share.isPending}
              onClick={() => fileId && share.mutate({ fileId, scope: "LINK" })}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-zinc-200 px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-60 dark:border-border dark:text-foreground dark:hover:bg-muted"
            >
              <LinkIcon className="h-4 w-4" />
              Gerar link
            </button>
            <p className="mt-1.5 text-xs text-zinc-400 dark:text-muted-foreground">
              Qualquer pessoa com o link acessa o arquivo, sem precisar de conta. Expira em{" "}
              {LINK_SHARE_TTL_DIAS} dias.
            </p>
          </div>

          <div>
            <p className="mb-1.5 text-[11px] uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
              Compartilhamentos ativos
            </p>
            {sharesQuery.isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-brand-600" />
              </div>
            ) : shares.length === 0 ? (
              <p className="text-xs text-zinc-400 dark:text-muted-foreground">Nenhum compartilhamento ainda.</p>
            ) : (
              <ul className="space-y-1.5">
                {shares.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 px-2.5 py-1.5 text-sm dark:border-border"
                  >
                    <span className="min-w-0 truncate text-zinc-700 dark:text-foreground">
                      {s.scope === "ORGANIZATION" && (s.targetOrg?.name ?? "Organização")}
                      {s.scope === "USER" && (s.targetUser?.name ?? "Usuário")}
                      {s.scope === "LINK" && "Link público"}
                      {s.expiresAt && (
                        <span className="ml-1.5 text-xs text-zinc-400 dark:text-muted-foreground">
                          · {descreverPrazo(s.expiresAt)}
                        </span>
                      )}
                    </span>
                    <div className="flex shrink-0 items-center gap-1">
                      {s.scope === "LINK" && s.linkToken && (
                        <button
                          type="button"
                          onClick={() => handleCopyLink(s.linkToken!)}
                          className="flex h-6 w-6 items-center justify-center rounded text-zinc-400 hover:text-brand-600"
                          aria-label="Copiar link"
                        >
                          {copiedToken === s.linkToken ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => unshare.mutate({ shareId: s.id })}
                        className="flex h-6 w-6 items-center justify-center rounded text-zinc-400 hover:text-red-600"
                        aria-label="Revogar"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
